import { useMemo } from "react";

import { ProtectedLink } from "@impero-com/common/components/protectedLink/index";
import * as Cmp from "@impero-com/common/utils/comparison";
import { parseDate } from "@impero-com/common/utils/date";
import {
    formatDate,
    formatUserName,
} from "@impero-com/common/utils/formatting";
import { TFunction, useTranslation } from "@impero-com/common/utils/i18n";
import { assertNever } from "@impero-com/common/utils/obj";
import {
    TST_BLESSED_ID_COLUMN_SELECTION,
    TST_COLUMN_SIZE,
    TST_DEFAULT_PAGE_SIZE,
} from "@impero-com/ui";
import {
    FilterTarget,
    usePresetTableSearchParams,
    ZodCodecMapToType,
} from "@impero-com/ui/domain";
import * as Sentry from "@sentry/react";
import {
    createColumnHelper,
    getCoreRowModel,
    getFacetedMinMaxValues,
    getFilteredRowModel,
    getSortedRowModel,
    useReactTable,
    getFacetedUniqueValues,
    getPaginationRowModel,
    Column,
} from "@tanstack/react-table";
import { Checkbox } from "antd";
import { keyBy } from "lodash-es";
import { Link } from "react-router-dom";

import {
    CategoryAndTag,
    ControlProgramId,
    ControlResultAssignmentKind,
    ControlResultOverviewListDto,
    ControlResultStepGroupWithoutFields,
    ControlResultUserPoolOverview,
    StepIndex,
    UserOverviewDtoContent,
} from "api/generated/model";
import { CustomEllipsisLabel } from "components/table/tsTable/CustomEllipsisLabel";
import { TSTValidMultipleOptionFilterSelect } from "components/table/tsTable/TSTValidMultipleOptionFilterSelect";
import { TSTValidStringTagsFilter } from "components/table/tsTable/TSTValidStringTagsFilter";
import { TagAndCategoryListFromRecord } from "components/tag/tagAndCategory/tagAndCategoryListFromRecord";
import { buildControlProgramViewUrl } from "org/control/controlProgram/urlUtils";
import { tagsTSTSortingFn } from "org/tags/tagsTSTUtils";
import { WebRoute } from "routes";
import { canViewControlResult } from "utils/actions";
import { createFilterFnWithMode } from "utils/table/createFilterFnWithMode";
import { createFilterHelpersGetter } from "utils/table/getTypedFilterValue";
import { tstArrIncludesSomeFilterFn } from "utils/table/tstArrIncludesSomeFilterFn";
import { matchesCategoryAndTagFilterFn } from "utils/tags/matchesCategoryAndTagFilterFn";
import { formatTagsRecordToString } from "utils/tags/tagRecord";

import { LegacyRoute } from "../../../legacyRoutes";
import { localizeAssignedAs } from "../utils/localization";

import {
    CONTROL_RESULT_LIST_COLUMN_IDS,
    controlResultListColumnFiltersCodecMap,
    controlResultListTableDefaultValues,
    controlResultTableStorageKey,
    DisplayLinkType,
} from "./consts";

export interface ControlResultRow {
    controlResultId: string;
    title: string;
    canView: boolean;
    controlProgramId: ControlProgramId;
    controlProgramName: string;
    controlProgramCanView: boolean;
    dueDate: string;
    stepIndex: StepIndex;
    assignedAs: string;
    tags: Record<string, string[]>;
    poolName: string | null;
    claimantName: string | null;
    claimantUserId: number | null;
}

function finalDueDate(stepGroup: ControlResultStepGroupWithoutFields): string {
    const { subsequentSteps, initialStep } = stepGroup;
    if (subsequentSteps.length === 0) {
        return initialStep.dueDate;
    }
    return subsequentSteps[subsequentSteps.length - 1].dueDate;
}

function stepBelongsToUser(
    kind: ControlResultAssignmentKind,
    userId: number,
    linkType: DisplayLinkType,
    poolsById: Record<string, ControlResultUserPoolOverview>
): boolean {
    switch (linkType) {
        case "direct":
            return kind.type === "User" && kind.userId === userId;
        case "pool":
            if (kind.type !== "Pool") {
                return false;
            }
            const pool = poolsById[kind.userPoolId];
            return pool ? pool.userIds.includes(userId) : false;
        default:
            assertNever(linkType);
    }
}

function buildRows(
    t: TFunction,
    data: ControlResultOverviewListDto,
    userId: number,
    linkType: DisplayLinkType
): ControlResultRow[] {
    const { controlPrograms, controlResultUserPools, users } =
        data.linkedResources;
    const poolsById = keyBy(controlResultUserPools, (pool) => pool.id);
    const usersById: Record<number, UserOverviewDtoContent> = keyBy(
        users,
        (user) => user.resource.id
    );

    const resolveProgram = (controlProgramId: ControlProgramId) => {
        const programDto = controlPrograms.find(
            (dto) => dto.resource.id === controlProgramId
        );
        return {
            controlProgramName: programDto?.resource.name ?? "",
            controlProgramCanView: programDto ? programDto.actions.view : false,
        };
    };

    return data.content.flatMap(({ resource: cr, actions }) => {
        const { controlProgramName, controlProgramCanView } = resolveProgram(
            cr.controlProgramId
        );
        const dueDate = finalDueDate(cr.stepGroup);
        const canView = canViewControlResult(actions);
        const { subsequentSteps, initialStep } = cr.stepGroup;

        const steps: {
            stepIndex: StepIndex;
            kind: ControlResultAssignmentKind;
        }[] = [
            { stepIndex: "Step0", kind: initialStep.kind },
            ...(subsequentSteps[0] // Step 1 -> First reviewer
                ? [
                      {
                          stepIndex: "Step1" as const,
                          kind: subsequentSteps[0].kind,
                      },
                  ]
                : []),
            ...(subsequentSteps[1] // Step 2 -> Second reviewer
                ? [
                      {
                          stepIndex: "Step2" as const,
                          kind: subsequentSteps[1].kind,
                      },
                  ]
                : []),
        ];

        return steps
            .filter(({ kind }) =>
                stepBelongsToUser(kind, userId, linkType, poolsById)
            )
            .map(({ stepIndex, kind }) => {
                const pool =
                    kind.type === "Pool" ? poolsById[kind.userPoolId] : null;
                const claimantUserId = pool?.claimantUserId ?? null;
                const claimantDto =
                    claimantUserId !== null
                        ? usersById[claimantUserId]
                        : undefined;
                return {
                    controlResultId: cr.id,
                    title: cr.title,
                    canView,
                    controlProgramId: cr.controlProgramId,
                    controlProgramName,
                    controlProgramCanView,
                    dueDate,
                    stepIndex,
                    assignedAs: localizeAssignedAs(t, stepIndex),
                    tags: cr.tags,
                    poolName: pool?.name ?? null,
                    claimantName: claimantDto
                        ? formatUserName(claimantDto.resource)
                        : null,
                    claimantUserId: claimantDto?.resource.id ?? null,
                };
            });
    });
}

export function useUserControlResultListTable(
    data: ControlResultOverviewListDto,
    userId: number,
    linkType: DisplayLinkType
) {
    const { t } = useTranslation();

    const tableData = useMemo(
        () => buildRows(t, data, userId, linkType),
        [t, data, userId, linkType]
    );
    const notificationLabel = t("Table_RemovedFiltersWarning");
    const tableId = controlResultTableStorageKey(linkType);

    const stateAndOnChanges = usePresetTableSearchParams(
        tableId,
        controlResultListTableDefaultValues,
        controlResultListColumnFiltersCodecMap,
        notificationLabel,
        Sentry.captureException
    );
    const columns = useMemo(() => buildColumns(t, linkType), [t, linkType]);
    const table = useReactTable({
        ...stateAndOnChanges,
        data: tableData,
        columns,
        getRowId: (row) => `${row.controlResultId}-${row.stepIndex}`,
        initialState: {
            ...stateAndOnChanges.initialState,
            pagination: {
                pageSize: TST_DEFAULT_PAGE_SIZE,
                pageIndex: 0,
            },
        },
        enableSortingRemoval: false,
        getCoreRowModel: getCoreRowModel<ControlResultRow>(),
        getSortedRowModel: getSortedRowModel<ControlResultRow>(),
        getFilteredRowModel: getFilteredRowModel<ControlResultRow>(),
        getFacetedMinMaxValues: getFacetedMinMaxValues<ControlResultRow>(),
        getFacetedUniqueValues: getFacetedUniqueValues<ControlResultRow>(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    const filterComponent = (
        column: Column<ControlResultRow>,
        target: FilterTarget
    ) => getFilterComponent(t, column, target);

    return {
        table,
        getFilterComponent: filterComponent,
        canAddException: (column: Column<ControlResultRow>) =>
            column.id === CONTROL_RESULT_LIST_COLUMN_IDS.TAGS,
        tableId,
    };
}

const columnHelper = createColumnHelper<ControlResultRow>();
function buildColumns(t: TFunction, linkType: DisplayLinkType) {
    return [
        columnHelper.display({
            id: TST_BLESSED_ID_COLUMN_SELECTION,
            header: (info) => (
                <Checkbox
                    checked={info.table.getIsAllPageRowsSelected()}
                    indeterminate={info.table.getIsSomePageRowsSelected()}
                    onChange={info.table.getToggleAllRowsSelectedHandler()}
                />
            ),
            size: TST_COLUMN_SIZE.CHECKBOX,
            meta: { fixedWidth: true },
            cell: (info) => (
                <Checkbox
                    checked={info.row.getIsSelected()}
                    onClick={() => info.row.toggleSelected()}
                />
            ),
        }),
        columnHelper.accessor((row) => row.title, {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.ACTIVITY,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={`${LegacyRoute.CONTROL_RESULT}/${row.controlResultId}/`}
                        hasAccess={row.canView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlResultRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_RESULT_LIST_COLUMN_IDS.ACTIVITY,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_ControlResults_Activity"),
            size: TST_COLUMN_SIZE.L,
        }),

        columnHelper.accessor((row) => row.controlProgramName, {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.CONTROL_PROGRAM,
            cell: (info) => {
                const row = info.row.original;
                return (
                    <ProtectedLink
                        href={buildControlProgramViewUrl(row.controlProgramId)}
                        hasAccess={row.controlProgramCanView}
                    >
                        {info.getValue()}
                    </ProtectedLink>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlResultRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_RESULT_LIST_COLUMN_IDS.CONTROL_PROGRAM,
                        value
                    )
            ),
            header: () => t("ControlProgram"),
            size: TST_COLUMN_SIZE.XL,
        }),

        columnHelper.accessor((row) => formatDate(parseDate(row.dueDate)), {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.DUE_DATE,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map(
                (row) => parseDate(row.original.dueDate),
                Cmp.date
            ),
            enableColumnFilter: false,
            header: () => t("AccessAndAssignment_ControlResults_DueDate"),
            size: TST_COLUMN_SIZE.M,
        }),
        ...(linkType === "pool" ? buildPoolColumns(t) : []),
        columnHelper.accessor((row) => row.assignedAs, {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.ASSIGNED_AS,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: Cmp.map((row) => row.original.stepIndex),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlResultRow, string[]>(
                (row, value) => value.includes(row.original.stepIndex)
            ),
            header: () => t("AccessAndAssignment_Controls_AssignedAs"),
            size: TST_COLUMN_SIZE.M,
        }),

        columnHelper.accessor((row) => formatTagsRecordToString(row.tags), {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.TAGS,
            cell: (info) => (
                <TagAndCategoryListFromRecord tags={info.row.original.tags} />
            ),
            enableSorting: true,
            sortingFn: (rowA, rowB) =>
                tagsTSTSortingFn(
                    Object.values(rowA.original.tags).flat(),
                    Object.values(rowB.original.tags).flat()
                ),
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<
                ControlResultRow,
                CategoryAndTag[]
            >((row, value) =>
                matchesCategoryAndTagFilterFn(row.original.tags, value)
            ),
            header: () => t("Tags"),
            size: TST_COLUMN_SIZE.L,
        }),
    ];
}

function buildPoolColumns(t: TFunction) {
    return [
        columnHelper.accessor((row) => row.poolName ?? "", {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.POOL,
            cell: (info) => <span>{info.getValue()}</span>,
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlResultRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_RESULT_LIST_COLUMN_IDS.POOL,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Controls_Pool"),
            size: TST_COLUMN_SIZE.M,
        }),

        columnHelper.accessor((row) => row.claimantName ?? "", {
            id: CONTROL_RESULT_LIST_COLUMN_IDS.CLAIMANT,
            cell: (info) => {
                const row = info.row.original;
                if (row.claimantUserId === null) {
                    return null;
                }
                return (
                    <Link to={`${WebRoute.USERS}/${row.claimantUserId}`}>
                        {info.getValue()}
                    </Link>
                );
            },
            enableSorting: true,
            sortingFn: "alphanumericCaseSensitive",
            enableColumnFilter: true,
            filterFn: createFilterFnWithMode<ControlResultRow, string[]>(
                (row, value) =>
                    tstArrIncludesSomeFilterFn(
                        row,
                        CONTROL_RESULT_LIST_COLUMN_IDS.CLAIMANT,
                        value
                    )
            ),
            header: () => t("AccessAndAssignment_Controls_Claimant"),
            size: TST_COLUMN_SIZE.M,
        }),
    ];
}

type ControlResultListColumnFilters = ZodCodecMapToType<
    typeof controlResultListColumnFiltersCodecMap
>;

type ControlResultFilterableColumnId = keyof ControlResultListColumnFilters;

function getFilterComponent(
    t: TFunction,
    column: Column<ControlResultRow>,
    target: FilterTarget
) {
    const getFilterHelpers =
        createFilterHelpersGetter<ControlResultListColumnFilters>(column);
    const columnId = column.id as ControlResultFilterableColumnId;

    switch (columnId) {
        case CONTROL_RESULT_LIST_COLUMN_IDS.ACTIVITY:
        case CONTROL_RESULT_LIST_COLUMN_IDS.CONTROL_PROGRAM: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()].map((option) => ({
                label: option,
                value: option,
                customLabel: <CustomEllipsisLabel text={option} />,
            }));
            const placeholderKey = {
                [CONTROL_RESULT_LIST_COLUMN_IDS.ACTIVITY]:
                    "AccessAndAssignment_ControlResults_Activity",
                [CONTROL_RESULT_LIST_COLUMN_IDS.CONTROL_PROGRAM]:
                    "ControlProgram",
            } as const;
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(placeholderKey[columnId])}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_RESULT_LIST_COLUMN_IDS.POOL:
        case CONTROL_RESULT_LIST_COLUMN_IDS.CLAIMANT: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const values: Map<string, number> = column.getFacetedUniqueValues();
            const options = [...values.keys()]
                .filter((option) => option !== "")
                .map((option) => ({
                    label: option,
                    value: option,
                }));
            const placeholderKey = {
                [CONTROL_RESULT_LIST_COLUMN_IDS.POOL]:
                    "AccessAndAssignment_Controls_Pool",
                [CONTROL_RESULT_LIST_COLUMN_IDS.CLAIMANT]:
                    "AccessAndAssignment_Controls_Claimant",
            } as const;
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={options}
                    placeholder={t(placeholderKey[columnId])}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_RESULT_LIST_COLUMN_IDS.ASSIGNED_AS: {
            const { value, setFilterValue } = getFilterHelpers(columnId);
            const stepIndexes = new Set(
                column
                    .getFacetedRowModel()
                    .flatRows.map((row) => row.original.stepIndex)
            );
            return (
                <TSTValidMultipleOptionFilterSelect
                    value={value}
                    column={column}
                    options={[...stepIndexes].map((stepIndex) => ({
                        label: localizeAssignedAs(t, stepIndex),
                        value: stepIndex,
                    }))}
                    placeholder={t("AccessAndAssignment_Controls_AssignedAs")}
                    setFilterValue={setFilterValue}
                />
            );
        }
        case CONTROL_RESULT_LIST_COLUMN_IDS.TAGS: {
            const { value, setFilterValue } = getFilterHelpers(
                columnId,
                target
            );
            const tagsPerRow = column
                .getFacetedRowModel()
                .flatRows.map((row) => row.original.tags);
            return (
                <TSTValidStringTagsFilter
                    placeholder={t("Tags")}
                    tags={tagsPerRow}
                    setFilterValue={setFilterValue}
                    column={column}
                    value={value}
                />
            );
        }
        default:
            return assertNever(columnId);
    }
}
